import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useEmployee } from "@/context/EmployeeContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Plus, Trash2, Globe, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_COLORS = [
  "#2563EB", "#7C3AED", "#DB2777", "#DC2626",
  "#EA580C", "#CA8A04", "#16A34A", "#0891B2",
];

export const Projects = () => {
  const { employee } = useEmployee();
  const [projects, setProjects] = useState([]);
  const [newProject, setNewProject] = useState({ name: "", color: "#2563EB" });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects?employee_code=${employee.code}`);
      setProjects(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newProject.name.trim()) {
      toast.error("Bitte Namen eingeben");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/projects`, {
        ...newProject,
        employee_code: employee.code,
        is_global: false,
      });
      toast.success("Einrichtung/Station/WB erstellt");
      setNewProject({ name: "", color: "#2563EB" });
      fetchProjects();
    } catch (error) {
      toast.error("Fehler beim Erstellen");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/projects/${id}`);
      toast.success("Eintrag gelöscht");
      fetchProjects();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  // Separate global and personal projects
  const globalProjects = projects.filter(p => p.is_global);
  const personalProjects = projects.filter(p => !p.is_global);

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Page Header */}
      <div>
        <h2 className="text-3xl font-bold text-slate-900 tracking-tight font-['Manrope']" data-testid="projects-title">
          Einrichtungen / Stationen / WB
        </h2>
        <p className="text-slate-500 mt-1">
          Wählen Sie aus vorgegebenen Einrichtungen oder erstellen Sie eigene
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* New Project Form */}
        <Card className="lg:col-span-1 zeitflow-card" data-testid="new-project-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Plus className="w-5 h-5 text-blue-600" />
              Eigene hinzufügen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="project_name">Bezeichnung</Label>
                <Input
                  id="project_name"
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                  placeholder="z.B. Station A, WB Nord"
                  data-testid="project-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Farbe wählen</Label>
                <div className="flex flex-wrap gap-2" data-testid="color-picker">
                  {PROJECT_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewProject({ ...newProject, color })}
                      className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${
                        newProject.color === color ? "ring-2 ring-offset-2 ring-blue-600" : ""
                      }`}
                      style={{ backgroundColor: color }}
                      data-testid={`color-${color}`}
                    />
                  ))}
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={isLoading}
                data-testid="create-project-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Hinzufügen
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Projects Lists */}
        <div className="lg:col-span-2 space-y-6">
          {/* Global Projects */}
          <Card className="zeitflow-card" data-testid="global-projects-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Globe className="w-5 h-5 text-purple-600" />
                Vorgegebene Einrichtungen ({globalProjects.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {globalProjects.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Globe className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Keine vorgegebenen Einrichtungen vorhanden</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {globalProjects.map((project) => (
                    <div
                      key={project.id}
                      className="flex items-center justify-between p-3 rounded-lg border border-purple-100 bg-purple-50/50"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: project.color }}
                        />
                        <span className="font-medium text-slate-900">{project.name}</span>
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                          Vorgegeben
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Personal Projects */}
          <Card className="zeitflow-card" data-testid="personal-projects-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="w-5 h-5 text-blue-600" />
                Meine Einrichtungen ({personalProjects.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {personalProjects.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Building2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Noch keine eigenen Einrichtungen</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {personalProjects.map((project, index) => (
                    <div
                      key={project.id}
                      className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                      data-testid={`project-item-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: project.color }}
                        />
                        <span className="font-medium text-slate-900">{project.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(project.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-project-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Projects;
